<template>
  <b-container fluid class="">
    <h1 class="m-3">
      {{ title }}
    </h1>
    <commonActions :data-ready="dataReadyTables" :filename-prefix="title"
      @commonFiltersUpdated="commonFiltersUpdated($event)" @clickDownloadPdf="downloadPdf($event)"
      @clickDownloadExcel="downloadExcel($event)" @clickDownloadCsv="downloadCsv($event)" />
    <table-renderer v-for="data of tablesData" :key="data.tableNumber" :table-number="data.tableNumber"
      ref="tableRenderers" :data-table="data.dataTable" :measure-fields="measureColumnsTables"
      :table-header="data.tableHeader" :group-headers="groupHeaders" :data-ready="dataReadyTables"
      :custom-table-class="customTableClass" />
  </b-container>
</template>

<script>
import Tools from "@/utils/tools";
import TableTools from "@/utils/tableTools";
import paths from "@/router/pathConstants";
import TableRenderer from "@/components/TableRenderer.vue";
import commonActions from "@/components/pages/commonActions";
import mesureEnum from "@/model/mesureEnum";
import dimensionEnum from "@/model/dimensionEnum";
import {
  columnTypeEnum,
  calculatedColumnsEnum,
  alternativeColumnLabelEnum,
} from "@/model/columnsEnums";
import { GamesSimple } from "@/router/routeNames";
import abortMixin from "@/mixins/abortMixin";

export default {
  name: GamesSimple,
  mixins: [abortMixin],
  components: {
    TableRenderer,
    commonActions,
  },
  data() {
    return {
      /** Tableau de filtres tels qu'attendu par cubeJs.
       * Ex :
       *    [
       *      {
       *        dimension: "Site.label",
       *        operator: "equals",
       *        values: ["Site 1"]
       *      },
       *      {
       *        dimension: "BusinessDate.id",
       *        operator: "inDateRange",
       *        values: ["2020-09-23", "2020-09-26"]
       *      },
       */
      commonFilters: [],
      timeDimension: null,
      siteValues: [],
      dataReadyTables: false,
      measureColumnsTables: [],
      groupHeaders: [],
      tablesData: [],
    };
  },
  computed: {
    title() {
      return this.$t("title.games_simple");
    },
    customTableClass() {
      return "table-width-medium";
    },
  },
  mounted() {
    this.initDisplayTables();
  },
  methods: {
    commonFiltersUpdated(value) {
      this.commonFilters = value.filters;
      this.timeDimension = value.timeDimension || null;
      this.siteValues = value.siteValues;
      this.updateTables();
    },
    updateTables() {
      var dimensions = [dimensionEnum.SITE, dimensionEnum.SITE_NAME, dimensionEnum.SITE_CURRENCY];
      this.updateTableList(this.commonFilters, dimensions);
    },
    async updateTableList(filters, dimensions) {
      try {
        const { signal } = this.resetAbortController();
        var self = this;
        self.dataReadyTables = false;

        self.tablesData = [];
        // Initialisation des objets contenant toutes les données pour chaque site :
        // permet d'afficher dès maintenant les entêtes de tableaux et le loader pour indiquer que les requêtes sont en cours
        self.siteValues.forEach((site, i) => {
          var table = {
            tableNumber: i.toString(),
            tableHeader: site.label,
            tableHeaderId: site.id,
            dataTable: [],
          };
          self.tablesData.push(table);
        });

        var queries = [];

        queries.push({
          query: {
            measures: [mesureEnum.ADJUSTMENTHISTORY_TOKENCREDITED],
            timeDimensions: this.timeDimension ? [this.timeDimension] : [],
            dimensions: dimensions,
            filters: filters,
          },
        });

        queries.push({
          query: {
            measures: [mesureEnum.STAFFGAMEHISTORY_TOKENCREDITED],
            timeDimensions: this.timeDimension ? [this.timeDimension] : [],
            dimensions: dimensions,
            filters: filters,
          },
        });

        queries.push({
          query: {
            measures: [
              mesureEnum.GAMEHISTORY_TOKEN_PRICE,
              mesureEnum.GAMEHISTORY_TOKEN_PRICE_HT,
              mesureEnum.GAMEHISTORY_TOTAL_VAT,
              mesureEnum.GAMEHISTORY_TOKEN,
              mesureEnum.GAMEHISTORY_BONUSTOKEN,
            ],
            timeDimensions: this.timeDimension
              ? [{ ...this.timeDimension, dimension: dimensionEnum.GAMEHISTORY_DATETIME }]
              : [],
            dimensions: dimensions,
            filters: filters,
          },
        });

        var resultSets = await TableTools.Helper.doMultipleRequest(queries, { signal });
        var mergedResults = TableTools.Helper.mergeResultsByCommonDimensions(
          resultSets,
          dimensions
        );

        // Extraire la première devise pour les labels de colonnes
        const defaultCurrency = mergedResults.length > 0 ? (mergedResults[0][dimensionEnum.SITE_CURRENCY] || 'EUR') : 'EUR';
        self.initDisplayTables(defaultCurrency);

        // Complétion des objets avec les données reçues pour chaque site
        self.siteValues.forEach((site) => {
          var table = self.tablesData.find(
            (tab) => tab.tableHeaderId === site.id
          );
          table.dataTable = mergedResults.filter(
            (result) => result[dimensionEnum.SITE] === site.id
          );

          // Extraire la devise spécifique à ce site
          const siteCurrency = table.dataTable.length > 0 ? (table.dataTable[0][dimensionEnum.SITE_CURRENCY] || 'EUR') : 'EUR';

          // Ajouter la devise à chaque ligne pour le formatage
          table.dataTable.forEach(row => {
            row["Site.currency"] = siteCurrency;
          });

          table.dataTable.forEach((data) => {
            // Rename columns for alternative labels
            TableTools.Helper.renameKey(
              data,
              mesureEnum.GAMEHISTORY_TOKEN,
              alternativeColumnLabelEnum.GAMEHISTORY_TOKEN
            );
            TableTools.Helper.renameKey(
              data,
              mesureEnum.GAMEHISTORY_TOKEN_PRICE,
              alternativeColumnLabelEnum.GAMEHISTORY_TOKEN_PRICE
            );
            TableTools.Helper.renameKey(
              data,
              mesureEnum.GAMEHISTORY_TOKEN_PRICE_HT,
              alternativeColumnLabelEnum.GAMEHISTORY_TOKEN_PRICE_HT
            );

            // Add calculated column for bonus token percentage
            const tokens = data[alternativeColumnLabelEnum.GAMEHISTORY_TOKEN] || 0;
            const bonusToken = data[mesureEnum.GAMEHISTORY_BONUSTOKEN] || 0;
            const totalTokens = tokens + bonusToken;
            data[calculatedColumnsEnum.PERCENT_BONUS_TOKEN] =
              totalTokens !== 0
                ? (bonusToken * 100) / totalTokens
                : 0;
          });
        });

        self.dataReadyTables = true;
      } catch (error) {
        if (this.isAbortError(error)) return;
        Tools.Alerts.Error(this.$swal, error);
      }
    },
    /** Initialise ce qui concerne l'affichage commun à tous les tableaux de ce rapport : quelles colonnes doivent réellement être affichées, dans quel ordre
     */
    initDisplayTables(siteCurrency = 'EUR') {
      const self = this;
      self.measureColumnsTables = TableTools.Helper.constructColumnTable(
        [
          alternativeColumnLabelEnum.GAMEHISTORY_TOKEN_PRICE_HT,
          alternativeColumnLabelEnum.GAMEHISTORY_TOKEN_PRICE,
          mesureEnum.GAMEHISTORY_TOTAL_VAT,
          alternativeColumnLabelEnum.GAMEHISTORY_TOKEN,
          mesureEnum.GAMEHISTORY_BONUSTOKEN,
          calculatedColumnsEnum.PERCENT_BONUS_TOKEN
        ],
        null,
        columnTypeEnum.MEASURE,
        false,
        null,
        false,
        siteCurrency
      );

      self.groupHeaders = [
        { nbOfMeasures: 6, label: self.$t("group.consumedTokens") },
      ];
    },
    downloadPdf(data) {
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_PDF, "_blank");
      }
    },
    downloadExcel(data) {
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_EXCEL, "_blank");
      }
    },
    downloadCsv(data) {
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_CSV, "_blank");
      }
    },
    storeExportData(filename, subtitles) {
      const self = this;
      var datasForExport = [];

      self.tablesData.forEach((table, index) => {
        const renderer = self.$refs.tableRenderers?.[index];
        const sortKey = renderer?.sortedColumn || self.defaultSortedColumnTables;

        const data = TableTools.Helper.assembleExportDataTable(
          table.dataTable,
          null,
          self.measureColumnsTables,
          self.groupHeaders,
          table.tableHeader,
          sortKey,
          null,
          null,
          table.tableNumber,
          self.customTableClass
        );
        datasForExport.push(data);
      });

      TableTools.Helper.storeExportData(
        datasForExport,
        self.title,
        subtitles,
        filename
      );
    },
  },
};
</script>
